import { isMacPlatform } from "#lib/platform.js";

const NON_TEXT_INPUT_TYPES = new Set(["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"]);

export function isEditableTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) {
		return false;
	}
	if (target instanceof HTMLInputElement) {
		return !NON_TEXT_INPUT_TYPES.has(target.type);
	}
	return target instanceof HTMLTextAreaElement || target.isContentEditable;
}

function isBlockedByModal(): boolean {
	return document.querySelector('[aria-modal="true"], :is(dialog:modal)') !== null;
}

type ModifierState = Pick<MouseEvent, "altKey" | "ctrlKey" | "metaKey">;

export interface CanvasClick extends ModifierState {
	shiftKey: boolean;
	pointerType?: string;
}

export function isPrimaryModifier(e: ModifierState, mac = isMacPlatform()): boolean {
	if (e.altKey) {
		return false;
	}
	return mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
}

export function classifyCanvasClick(e: CanvasClick, mac = isMacPlatform()): "replace" | "add" | null {
	if (e.pointerType === "touch" || !isPrimaryModifier(e, mac)) {
		return null;
	}
	return e.shiftKey ? "add" : "replace";
}

export interface Chord {
	key: string;
	primary?: boolean;
	shift?: boolean;
}

const LETTER = /^[a-z]$/i;
const NON_LATIN_LETTER = /^(?!\p{Script=Latin})\p{L}$/u;

function pressedLetter(e: KeyboardEvent): string | undefined {
	if (LETTER.test(e.key)) {
		return e.key.toLowerCase();
	}
	if (NON_LATIN_LETTER.test(e.key) && e.code.startsWith("Key")) {
		return e.code.slice(3).toLowerCase();
	}
	return undefined;
}

export function matchesChord(e: KeyboardEvent, chord: Chord, mac = isMacPlatform()): boolean {
	if (e.altKey) {
		return false;
	}
	const primary = isPrimaryModifier(e, mac);
	const neither = !e.metaKey && !e.ctrlKey;
	if (chord.primary ? !primary : !neither) {
		return false;
	}
	if (LETTER.test(chord.key)) {
		return e.shiftKey === Boolean(chord.shift) && pressedLetter(e) === chord.key;
	}
	return e.key === chord.key;
}

export function isDeleteKey(e: KeyboardEvent, mac = isMacPlatform()): boolean {
	return !e.shiftKey && (matchesChord(e, { key: "Backspace" }, mac) || matchesChord(e, { key: "Delete" }, mac));
}

export function shouldIgnoreShortcut(e: KeyboardEvent, { swallowWhileTyping = false } = {}): boolean {
	if (e.defaultPrevented || isBlockedByModal()) {
		return true;
	}
	if (!isEditableTarget(e.target)) {
		return false;
	}
	if (swallowWhileTyping) {
		e.preventDefault();
	}
	return true;
}
